import type { ReactNode } from "react";
import { Checkbox, ListRow, vars } from "@pack-it-up/ui-web";
import { SyncStatusBadge } from "./SyncStatusBadge.js";
import type { SyncEntry } from "@pack-it-up/core";

// Doc 13 §2.3 — rows the template has but the trip doesn't. Either the user deleted the
// imported row, or a co-owner added it to the template after this trip imported; the
// data model can't tell those apart, so both land here, pre-ticked. Ticked = keep it in
// the template; unticking is how a trip-side deletion travels back.

export interface SyncOnlyInTemplateRow {
  key: string;
  name: string;
  entry: SyncEntry;
}

export interface SyncOnlyInTemplateSectionProps {
  rows: SyncOnlyInTemplateRow[];
  isChecked: (key: string) => boolean;
  onToggle: (key: string) => void;
}

export const SyncOnlyInTemplateSection = ({ rows, isChecked, onToggle }: SyncOnlyInTemplateSectionProps): ReactNode => {
  if (rows.length === 0) return null;
  return (
    <section
      aria-label="Only in template"
      style={{
        marginTop: vars.space.lg,
        border: `1px dashed ${vars.color.border}`,
        borderRadius: vars.radius.md,
        background: vars.color.surface,
        overflow: "hidden",
        opacity: 0.85,
      }}
    >
      <div style={{ padding: `${vars.space.sm} ${vars.space.md}`, borderBottom: `1px solid ${vars.color.border}` }}>
        <div style={{ fontSize: vars.font.sizeSm, fontWeight: vars.font.weightMedium, color: vars.color.textMuted }}>
          Only in template
        </div>
        <div style={{ fontSize: vars.font.sizeXs, color: vars.color.textMuted, marginTop: "2px" }}>
          Ticked entries stay in the template. Untick one to remove it.
        </div>
      </div>
      {rows.map((r) => (
        <ListRow
          key={r.key}
          aria-label={r.name}
          leading={
            <Checkbox
              checked={isChecked(r.key)}
              onChange={() => onToggle(r.key)}
              aria-label={`Keep ${r.name} in the template`}
            />
          }
          trailing={<SyncStatusBadge entry={r.entry} />}
        >
          <span style={{ fontSize: "13.5px", color: vars.color.textMuted }}>{r.name}</span>
        </ListRow>
      ))}
    </section>
  );
};
